import mixpanel from "mixpanel-browser"
import { resolveOrderType } from "~/utils/orderType"
import { readImpersonation } from "~/utils/impersonation"

/**
 * The single Mixpanel facade for the hub. Only this facade (and the small
 * bootstrap in plugins/mixpanel.client.ts) should import `mixpanel-browser`;
 * every other call site goes through here so the naming/PII/money rules below are enforced.
 *
 * Three tiers of coverage (rationale: `docs/decisions.md` 2026-09-04 in the
 * and-dine-platform orchestration repo — hub itself has no docs/ directory):
 *   Tier 1 — curated business events, one literal name each (EVENTS below).
 *   Tier 2 — blanket UI coverage: page_viewed / ui_interaction /
 *            search_performed / filter_applied.
 *   Tier 3 — blanket Vuex write telemetry: store_action_completed, plus
 *            app_error_shown from the root handleError action.
 *
 * Hard rule: event names are NEVER constructed at runtime. Mixpanel treats
 * names as opaque strings, so a template literal produces an unbounded,
 * un-analysable Lexicon. The varying part always goes in a property
 * (entity_type, document_type, operation, change_type, ...).
 */

/* ------------------------------------------------------------------------- */
/* Event names                                                               */
/* ------------------------------------------------------------------------- */

export const EVENTS = Object.freeze({
	// --- Auth & session ---
	LOGIN_SUCCEEDED: "login_succeeded",
	LOGIN_FAILED: "login_failed",
	LOGOUT_PERFORMED: "logout_performed",
	SESSION_REFRESH_FAILED: "session_refresh_failed",
	IMPERSONATION_STARTED: "impersonation_started",
	PASSWORD_RESET_REQUESTED: "password_reset_requested",
	ACCOUNT_VERIFIED: "account_verified",

	// --- Order creation funnel (the Value Moment path) ---
	ORDER_CREATE_STARTED: "order_create_started",
	ORDER_MAKER_SELECTED: "order_maker_selected",
	ORDER_CUSTOMER_SELECTED: "order_customer_selected",
	ORDER_CART_ITEM_ADDED: "order_cart_item_added",
	ORDER_CART_ITEM_REMOVED: "order_cart_item_removed",
	ORDER_SHARE_CART_IMPORTED: "order_share_cart_imported",
	ORDER_CREATED: "order_created",

	// --- Order lifecycle ---
	ORDER_UPDATED: "order_updated",
	ORDER_CONTENTS_UPDATED: "order_contents_updated",
	ORDER_CANCELLED: "order_cancelled",
	ORDER_MARKED_PAID: "order_marked_paid",
	ORDER_STATUS_CHANGED: "order_status_changed",
	ORDER_PRINTED: "order_printed",
	ORDER_REORDERED: "order_reordered",

	// --- Delivery / PedalMe ---
	DELIVERY_BOOKING_STARTED: "delivery_booking_started",
	DELIVERY_QUOTE_CREATED: "delivery_quote_created",
	DELIVERY_BOOKING_CONFIRMED: "delivery_booking_confirmed",
	ADDRESS_PIN_NOT_PLACED: "address_pin_not_placed",
	ADDRESS_PIN_FAR_FROM_ADDRESS: "address_pin_far_from_address",

	// --- Finance & documents ---
	MAKER_INVOICES_GENERATED: "maker_invoices_generated",
	MAKER_INVOICE_EMAILS_SENT: "maker_invoice_emails_sent",
	INVOICE_REGENERATION_QUEUED: "invoice_regeneration_queued",
	DOCUMENT_DOWNLOADED: "document_downloaded",
	REPORT_QUEUED: "report_queued",
	BULK_FILE_GENERATION_QUEUED: "bulk_file_generation_queued",
	XERO_DOCUMENTS_QUEUED: "xero_documents_queued",

	// --- Catalogue & supply ---
	PRODUCT_SAVED: "product_saved",
	PRODUCT_DUPLICATED: "product_duplicated",
	CATALOGUE_ENTITY_SAVED: "catalogue_entity_saved",
	ENTITY_STATUS_TOGGLED: "entity_status_toggled",
	MAKER_SAVED: "maker_saved",
	MAKER_CONTRACT_RESENT: "maker_contract_resent",

	// --- Customers, orgs, teams, cost centres ---
	CUSTOMER_SAVED: "customer_saved",
	ORGANISATION_SAVED: "organisation_saved",
	ORGANISATION_MEMBERSHIP_CHANGED: "organisation_membership_changed",
	TEAM_CHANGED: "team_changed",
	COST_CENTRE_SAVED: "cost_centre_saved",

	// --- Users, settings, integrations, ops ---
	USER_SAVED: "user_saved",
	SETTING_SAVED: "setting_saved",
	SUB_CONFIG_SAVED: "sub_config_saved",
	INTEGRATION_CONNECTION_CHANGED: "integration_connection_changed",
	HUBSPOT_SYNC_RUN: "hubspot_sync_run",
	ATTACHMENT_CHANGED: "attachment_changed",
	AUDIT_LOG_VIEWED: "audit_log_viewed",

	// --- Tier 2: blanket UI ---
	PAGE_VIEWED: "page_viewed",
	UI_INTERACTION: "ui_interaction",
	SEARCH_PERFORMED: "search_performed",
	FILTER_APPLIED: "filter_applied",

	// --- Tier 3: blanket store telemetry ---
	STORE_ACTION_COMPLETED: "store_action_completed",
	APP_ERROR_SHOWN: "app_error_shown",
})

export type EventName = (typeof EVENTS)[keyof typeof EVENTS]

const KNOWN_EVENTS: ReadonlySet<string> = new Set(Object.values(EVENTS))

/* ------------------------------------------------------------------------- */
/* Environment gate                                                          */
/* ------------------------------------------------------------------------- */

export type Environment = "local" | "demo" | "staging" | "preview" | "production"

/**
 * Hub has no runtimeConfig — config is read as `import.meta.env.VITE_*` at the
 * call site. Environment is derived from the API host rather than the page
 * host, because every branch deploys to `<branch>.anddine.co.uk`, so the page
 * hostname cannot tell a preview build apart from production.
 */
export const resolveEnvironment = (apiBase?: string): Environment => {
	const base = (apiBase ?? "").trim()
	if (!base) return "local"

	// Exact hostname, not substring: "https://api.anddine.co.uk.attacker.example"
	// contains "//api.anddine.co.uk" and would otherwise resolve as production,
	// switching real tracking on for a host we do not control.
	let host: string
	try {
		host = new URL(base).hostname.toLowerCase()
	} catch {
		// Not a URL at all — can't prove it's production, so don't treat it as such.
		return "local"
	}

	if (host === "localhost" || host === "127.0.0.1" || host === "::1") return "local"
	if (host === "staging-api.anddine.co.uk") return "staging"
	// origin/demo is a real long-lived deploy, not a PR preview — label it so
	// demo traffic is identifiable rather than hidden inside "preview".
	if (host === "demo-api.anddine.co.uk" || host === "demo.anddine.co.uk") return "demo"
	if (host === "api.anddine.co.uk") return "production"
	return "preview"
}

/* ------------------------------------------------------------------------- */
/* Module state                                                              */
/* ------------------------------------------------------------------------- */

let enabled = false
let environment: Environment = "local"

/**
 * True for the whole session once an admin has entered an impersonated
 * session. Blocks IDENTITY writes — anything derived from the TARGET's profile
 * (identify, people.set, group membership). It deliberately does NOT block
 * activity counters (people.increment / set_once), which are derived from the
 * admin's own actions and land on the admin's own distinct_id. Blocks: `loginAsMaker` swaps the
 * auth cookie for the target's token, so a naive `people.set` would overwrite
 * that maker's profile with the admin's session data.
 */
let profileWritesBlocked = false

/** In-memory timers for duration_ms. Deliberately NOT persisted — see timeEvent(). */
const timers = new Map<string, number>()

/**
 * Bumped every time handleError shows a banner. Vuex's `after` hook fires when
 * the dispatch RESOLVES, and nearly every action here swallows its error and
 * resolves anyway — so without this, a failed write emitted
 * store_action_completed and inflated the completion rate. Comparing the counter
 * across an action's window tells us whether it actually succeeded.
 */
let errorSeq = 0

/** Called by the root handleError action. */
export const noteErrorShown = () => {
	errorSeq += 1
}

export const currentErrorSeq = () => errorSeq

/** Last-emitted timestamps for the tracking-level debounce. */
const lastEmitted = new Map<string, number>()

/**
 * Whether group analytics is on. Resolved once in initAnalytics rather than read
 * from import.meta.env at each call, so tests can drive it — reading the env
 * inline made every group-related assertion silently vacuous.
 */
let groupsOn = false

/** Durations above this are treated as "user walked away" and dropped. */
export const MAX_DURATION_MS = 30 * 60 * 1000

export const isEnabled = () => enabled
export const getEnvironment = () => environment
export const areProfileWritesBlocked = () => profileWritesBlocked

/* ------------------------------------------------------------------------- */
/* Property hygiene                                                          */
/* ------------------------------------------------------------------------- */

/**
 * Absence IS the signal. A property that is sometimes "" and sometimes missing
 * forces every Mixpanel filter to carry two clauses, and "-" is the hub's
 * table placeholder (`mapOrderToRow` renders `maker: order.maker?.name || "-"`)
 * which would otherwise masquerade as a real maker name.
 */
export const pruneProps = (props: Record<string, any> = {}): Record<string, any> => {
	const out: Record<string, any> = {}

	for (const [key, raw] of Object.entries(props)) {
		if (raw === null || raw === undefined) continue

		const value = typeof raw === "string" ? raw.trim() : raw
		if (value === "" || value === "-") continue
		if (typeof value === "number" && !Number.isFinite(value)) continue

		out[key] = value
	}

	return out
}

/**
 * Money reaches Mixpanel as **pounds to 2dp** (`*_gbp`), converted here from the
 * integer pence every store object holds. Reason: analysts read these reports
 * directly, and `order_total_pence: 12345` needed a mental (or formula) divide on
 * every single chart. `order_total_gbp: 123.45` is the actual money.
 *
 * The INPUT is still strictly integer pence, and display values are REJECTED,
 * not salvaged. Cart.vue's `lineTotal()` returns a pounds string and
 * `mapOrderToRow` returns `total: "£123.45"`; the first version of this helper
 * stripped the symbol and returned 123 — silently 100x too small. A
 * plausible-looking wrong number is far worse than a missing one. So conversion
 * happens in exactly one place, from one known unit, and anything ambiguous is
 * dropped.
 */
export const moneyGbp = (pence: unknown): number | undefined => {
	if (pence === null || pence === undefined || pence === "") return undefined

	let intPence: number
	if (typeof pence === "number") {
		if (!Number.isFinite(pence) || !Number.isInteger(pence)) return undefined
		intPence = pence
	} else {
		const text = String(pence).trim()
		// A currency symbol, separator or decimal point means this is a formatted
		// display value, not the integer pence this helper is documented to take.
		if (!/^-?\d+$/.test(text)) return undefined
		intPence = Number(text)
	}

	// 2dp exactly — no float tail. Mixpanel sums and averages this natively.
	return Math.round(intPence) / 100
}

/**
 * Difference between two `*_gbp` values, rounded to 2dp. Subtracting two 2dp
 * floats leaves a binary tail (123.45 - 100.10 = 23.349999999999998), which
 * would show up verbatim in Mixpanel.
 */
export const gbpDelta = (before?: number, after?: number): number | undefined => {
	if (before === undefined || after === undefined) return undefined
	return Math.round((after - before) * 100) / 100
}

/**
 * An entity id, kept in its ORIGINAL type. Ids are not uniform across this
 * codebase: orders/makers/packages are integers, but `Product.maker_id` is a
 * string UUID. Running those through numeric() returned undefined and pruneProps
 * then dropped the property, so product_saved silently lost its maker breakdown.
 * Mixpanel handles both types, so pass the value through rather than coerce it.
 */
export const entityId = (value: unknown): string | number | undefined => {
	if (value === null || value === undefined) return undefined
	if (typeof value === "number") return Number.isFinite(value) ? value : undefined
	const text = String(value).trim()
	return text === "" || text === "-" ? undefined : text
}

/** Coerce to a finite number, or drop it. `price_gross` arrives as a string from some API shapes. */
export const numeric = (value: unknown): number | undefined => {
	if (value === null || value === undefined || value === "") return undefined
	const n = Number(value)
	return Number.isFinite(n) ? n : undefined
}

/** Enum values are snake_case too, so the API's Title Case is mapped at the boundary. */
export const enumValue = (value: unknown): string | undefined => {
	if (value === null || value === undefined) return undefined

	// Reject the placeholders BEFORE slugifying — otherwise "-" becomes "_".
	const text = String(value).trim()
	if (text === "" || text === "-") return undefined

	return text.toLowerCase().replace(/[\s-]+/g, "_")
}

/**
 * Canonical `entity_type` value. The hub names the same entity three ways: the
 * API route segment (`productCategories`, `costCentres`), the audit-log prop
 * (`productCategory`, `makersCategory`) and the literal every hand-written
 * event already uses (`product_category`, `cost_centre`). enumValue() only
 * lowercases, so the first two landed as `productcategories` / `productcategory`
 * — three spellings of one entity in the Lexicon, and a breakdown that splits
 * three ways. Map them onto the literal.
 */
const ENTITY_TYPES: Readonly<Record<string, string>> = Object.freeze({
	// changeEntityState's payload.entity — the API route segment, plural.
	orders: "order",
	products: "product",
	productCategories: "product_category",
	makers: "maker",
	makerCategories: "maker_category",
	costCentres: "cost_centre",
	rewards: "reward",
	discounts: "discount",
	organisations: "organisation",
	packages: "package",
	// EntityTimeline's entity-type prop — singular, and not always the same word.
	costCentre: "cost_centre",
	productCategory: "product_category",
	makersCategory: "maker_category",
	makerCategory: "maker_category",
})

export const entityType = (value: unknown): string | undefined => {
	const raw = typeof value === "string" ? value.trim() : ""
	if (!raw) return undefined
	const mapped = ENTITY_TYPES[raw]
	if (mapped) return mapped
	// Unmapped: snake_case the camelCase rather than flattening it, so a new
	// entity lands as "some_entity" instead of an unreadable "someentity".
	return enumValue(raw.replace(/([a-z0-9])([A-Z])/g, "$1_$2"))
}

/**
 * Strips email addresses out of free text. `error_message` is built from
 * user-visible backend strings, and a validation error can easily quote the
 * address it rejected ("a@b.com is already registered") — which would put a
 * full email on an event despite the domain-only rule everywhere else.
 */
export const scrubEmails = (text: string): string =>
	text.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")

export const truncate = (value: unknown, max = 200): string | undefined => {
	if (value === null || value === undefined) return undefined
	const text = scrubEmails(String(value).trim())
	return text === "" ? undefined : text.slice(0, max)
}

/** YYYY-MM-DD, never the DD/MM/YY display format produced by $moment(...).format(...). */
export const dateOnly = (value: unknown): string | undefined => {
	if (!value) return undefined
	const date = new Date(value as any)
	return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10)
}

/**
 * Path-only URL. mixpanel-browser AUTOCAPTURES `$current_url` from
 * `window.location.href` and `$referrer` from `document.referrer` on every
 * single event, query string included — and hub URLs carry personal data:
 * `/verify/new?email=<full address>` (pages/verify/new.vue:68) and
 * `/forgot?activate_account=…`. The domain-only rule was only ever applied to
 * hand-written properties, which is exactly how site shipped invite emails to
 * Mixpanel for months (CLAUDE.md → Mixpanel Instrumentation → Never send).
 *
 * Nothing in a hub query string is worth the risk, so the query and hash are
 * dropped wholesale rather than allow-listed. Returns "" rather than undefined
 * for anything unparseable, because these values must ALWAYS override the
 * SDK's — omitting the key lets the raw URL straight through.
 */
export const sanitiseAnalyticsUrl = (url: unknown): string => {
	const text = typeof url === "string" ? url.trim() : ""
	if (!text) return ""
	try {
		const base = typeof window === "undefined" ? undefined : window.location?.href
		const parsed = new URL(text, base)
		return `${parsed.origin}${parsed.pathname}`
	} catch {
		return ""
	}
}

/**
 * The scrubbed replacements for the SDK's autocaptured URL properties.
 *
 * mixpanel-browser builds each event as extend({}, autocaptured, superProps,
 * eventProps), so event properties are the strongest override and that is what
 * track() uses. But SOME events never pass through track(): `mixpanel.identify()`
 * emits its own `$identify` with `skip_hooks: true`, which would keep the raw
 * query-string URL. registerUrlSuperProps() below covers those, since super
 * properties still beat autocapture.
 */
const scrubbedUrlProps = (): Record<string, string> => {
	if (typeof window === "undefined") return {}
	return {
		$current_url: sanitiseAnalyticsUrl(window.location?.href),
		$referrer: sanitiseAnalyticsUrl(typeof document === "undefined" ? "" : document.referrer),
	}
}

/**
 * Registers the scrubbed URLs as SUPER properties. Needed because `$identify`
 * (and anything else the SDK emits internally) bypasses track() and would
 * otherwise carry the raw `window.location.href`. Called at init and on every
 * navigation, so the value tracks the current page.
 */
export const registerUrlSuperProps = () => {
	if (!enabled) return
	const props = scrubbedUrlProps()
	if (Object.keys(props).length) registerSuperProps(props)
}

/** Domain only — we never send a full email address as an event property. */
export const emailDomain = (email: unknown): string | undefined => {
	const text = typeof email === "string" ? email.trim().toLowerCase() : ""
	const at = text.lastIndexOf("@")
	return at > -1 && at < text.length - 1 ? text.slice(at + 1) : undefined
}

/* ------------------------------------------------------------------------- */
/* Shared order properties                                                   */
/* ------------------------------------------------------------------------- */

/**
 * Spread into every order event so an analyst can break any of them down the
 * same way. Defined once here rather than at ~20 call sites.
 *
 * Every money field is read straight off the order record, which stores
 * integer pence. Never pass a table row through this: `mapOrderToRow` renders
 * `total: "£123.45"` and `maker: "-"` for display.
 */
export const orderProps = (order: any = {}) => {
	const items = Array.isArray(order.items)
		? order.items
		: Array.isArray(order.orderProducts)
			? order.orderProducts
			: []

	return pruneProps({
		order_id: entityId(order.id),
		order_reference: order.reference,
		order_type: enumValue(resolveOrderType(order).variant),
		order_status: enumValue(order.status),
		payment_status: enumValue(order.paid),
		maker_id: entityId(order.maker?.id ?? order.maker_id),
		maker_name: order.maker?.name,
		organisation_id: entityId(order.organisation_id),
		customer_id: entityId(order.customer_id),
		business_name: order.business_name,
		order_total_gbp: moneyGbp(order.total),
		items_gross_gbp: moneyGbp(order.items_gross),
		item_count: items.length,
		delivery_service: enumValue(order.delivery_service),
		delivery_date: dateOnly(order.delivery_date_time),
		has_discount: Boolean(order.discount_id ?? order.discount),
	})
}

/* ------------------------------------------------------------------------- */
/* Timing                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Arms a timer whose elapsed time lands as `duration_ms` on the next matching
 * track() call.
 *
 * Deliberately an in-memory Map rather than `mixpanel.time_event`. Mixpanel
 * persists its timers in localStorage, so an admin who opens the cancel modal
 * and walks away leaves a timer that attaches a 200,000s duration to the NEXT
 * real cancellation — days later. An in-memory timer simply dies with the tab,
 * which is the correct behaviour. Re-arming overwrites, so the last arming wins.
 */
export const timeEvent = (event: EventName) => {
	if (!enabled) return
	timers.set(event, Date.now())
}

const consumeDuration = (event: string): number | undefined => {
	const startedAt = timers.get(event)
	if (startedAt === undefined) return undefined

	timers.delete(event)
	const elapsed = Date.now() - startedAt

	// Drop the property, keep the event.
	if (elapsed < 0 || elapsed > MAX_DURATION_MS) return undefined
	return elapsed
}

export const clearTimers = () => timers.clear()

/* ------------------------------------------------------------------------- */
/* Core track                                                               */
/* ------------------------------------------------------------------------- */

export type TrackOptions = {
	/** Use sendBeacon when the caller is about to navigate or reload the page. */
	beacon?: boolean
	/** Suppress repeats within this window, keyed by event name + dedupeKey. */
	debounceMs?: number
	dedupeKey?: string
}

export const track = (
	event: EventName,
	props: Record<string, any> = {},
	options: TrackOptions = {}
) => {
	if (!enabled) return

	if (!KNOWN_EVENTS.has(event)) {
		// A name that isn't in EVENTS means someone built one at runtime.
		if (environment !== "production") {
			console.warn(
				`[analytics] unknown event name "${event}" — add it to EVENTS in utils/analytics.ts`
			)
		}
		return
	}

	if (options.debounceMs) {
		const key = `${event}::${options.dedupeKey ?? ""}`
		const previous = lastEmitted.get(key)
		const now = Date.now()
		if (previous !== undefined && now - previous < options.debounceMs) return
		lastEmitted.set(key, now)
	}

	// Only an actually-armed timer contributes duration_ms. Spreading it
	// unconditionally would clobber a caller-supplied duration (trackStoreAction
	// measures its own) with undefined.
	const duration = consumeDuration(event)
	const payload = pruneProps(duration === undefined ? props : { ...props, duration_ms: duration })

	// Applied AFTER pruneProps so an empty string survives: these keys must be
	// present on every event to override the SDK's autocaptured, query-carrying
	// values. See sanitiseAnalyticsUrl.
	Object.assign(payload, scrubbedUrlProps())

	try {
		mixpanel.track(event, payload, options.beacon ? { transport: "sendBeacon" } : undefined)
	} catch {
		// Analytics must never break the hub.
	}
}

/* ------------------------------------------------------------------------- */
/* Identity                                                                  */
/* ------------------------------------------------------------------------- */

export type HubUser = {
	id?: string
	first_name?: string
	last_name?: string
	email?: string
	phone?: string
	role?: string
	is_active?: boolean
	last_login_at?: string | null
	createdAt?: string
	makers?: Array<{ id?: string | number; name?: string; slug?: string }>
}

export const superPropsForUser = (user: HubUser) => {
	const makers = Array.isArray(user.makers) ? user.makers : []
	const isAdmin = user.role === "user"

	return pruneProps({
		user_role: user.role,
		is_admin: isAdmin,
		user_email_domain: emailDomain(user.email),
		maker_count: makers.length,
		// A maker's own maker; omitted for admins, who belong to none.
		primary_maker_id: isAdmin ? undefined : makers[0]?.id,
		primary_maker_name: isAdmin ? undefined : makers[0]?.name,
		primary_maker_slug: isAdmin ? undefined : makers[0]?.slug,
	})
}

export const registerSuperProps = (props: Record<string, any>) => {
	if (!enabled) return
	try {
		mixpanel.register(pruneProps(props))
	} catch {
		/* no-op */
	}
}

/**
 * THE single entry point for "a user profile just resolved". `getUser` and the
 * layout's `user` watcher both call this, so no call site can skip the
 * impersonation guard — every identity, profile and group write is behind it.
 *
 * Do not call `identifyUser` / `setMakerGroups` / `registerSuperProps(
 * superPropsForUser(...))` directly from application code; call this.
 */
export const applyUserContext = (user: HubUser) => {
	if (!enabled || !user?.id) return

	// Cross-tab fail-closed check. The auth cookie is shared between tabs, but
	// `profileWritesBlocked` is per-tab module memory — so a tab that was already
	// open when another started impersonating would be unblocked while the cookie
	// holds the TARGET's token, and would identify and profile-write them. The
	// localStorage record is the shared state, so consult it: if one exists and
	// this profile isn't the impersonator's own, adopt the block here too.
	if (!profileWritesBlocked) {
		const record = readImpersonation()
		if (record && record.impersonator_user_id !== user.id) {
			beginImpersonation(record)
		}
	}

	if (profileWritesBlocked) {
		// We are impersonating. `user` is the TARGET's profile, but the identified
		// distinct_id is deliberately still the admin's. Registering the target's
		// role/maker as super properties would make the admin's own events look
		// like a maker's and silently poison "feature adoption by role" — one of
		// the reports this integration exists to produce. Record the target under
		// impersonated_* keys and leave the acting user's identity intact.
		const makers = Array.isArray(user.makers) ? user.makers : []
		registerSuperProps({
			is_impersonating: true,
			impersonated_user_role: user.role,
			impersonated_maker_id: makers[0]?.id,
			impersonated_maker_name: makers[0]?.name,
		})
		return
	}

	identifyUser(user)
	if (user.role === "maker") {
		setMakerGroups(user.makers)
	}
}

/**
 * Identify + profile. `user.id` is the UUID primary key — never the email,
 * which changes. Must run before any people.set (Mixpanel drops profile
 * writes made against an un-identified distinct_id).
 */
const identifyUser = (user: HubUser) => {
	if (!enabled || !user?.id) return

	try {
		// While impersonating, `user` is the TARGET's profile and the identified
		// distinct_id is deliberately still the admin's — so NOTHING here may run.
		// superPropsForUser() in particular would register the target's role and
		// maker onto the admin's session: the poison-by-role bug applyUserContext
		// exists to prevent. applyUserContext owns the impersonated branch.
		if (profileWritesBlocked) return

		mixpanel.identify(user.id)
		registerSuperProps(superPropsForUser(user))

		const makers = Array.isArray(user.makers) ? user.makers : []
		const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ")

		mixpanel.people.set(
			pruneProps({
				// The ONLY $-prefixed keys we ever write: Mixpanel's UI, exports and
				// cohort syncs key off these literals.
				$email: user.email,
				$name: fullName,
				$first_name: user.first_name,
				$last_name: user.last_name,
				$phone: user.phone,
				$created: user.createdAt,
				role: user.role,
				is_admin: user.role === "user",
				is_active: user.is_active,
				last_login_at: user.last_login_at,
				maker_count: makers.length,
				maker_ids: makers.map((maker) => maker.id).filter(Boolean),
				maker_names: makers.map((maker) => maker.name).filter(Boolean),
			})
		)
	} catch {
		/* no-op */
	}
}

export const setPeople = (props: Record<string, any>) => {
	if (!enabled || profileWritesBlocked) return
	try {
		mixpanel.people.set(pruneProps(props))
	} catch {
		/* no-op */
	}
}

/**
 * NOT blocked during impersonation. The block exists to stop TARGET-derived
 * identity data (name, email, role, maker) landing on a profile; these values
 * are derived from the action the admin just performed and land on the admin's
 * own distinct_id, so suppressing them only loses real data. Every event
 * carries `is_impersonating`, so an analyst can still exclude the session.
 */
export const setPeopleOnce = (props: Record<string, any>) => {
	if (!enabled) return
	try {
		mixpanel.people.set_once(pruneProps(props))
	} catch {
		/* no-op */
	}
}

/**
 * Lifetime counters. Deliberately NOT people.track_charge(): that writes
 * $transactions and implies the profiled user paid, but hub users are staff
 * creating orders somebody else pays for — it would corrupt every
 * out-of-the-box revenue report.
 */
export const incrementPeople = (props: Record<string, number>) => {
	// Not blocked during impersonation — see setPeopleOnce. These counters are
	// the acting admin's own activity, and blocking them made
	// impersonations_started_count permanently zero on the two paths that set it.
	if (!enabled) return
	try {
		mixpanel.people.increment(pruneProps(props))
	} catch {
		/* no-op */
	}
}

export type ImpersonationContext = {
	impersonator_user_id: string
	/** Set by readImpersonation when the record is past its TTL. */
	is_stale?: boolean
	impersonation_kind: string
	impersonated_user_id?: string
	/** Maker.id is a UUID — see ImpersonationRecord in utils/impersonation.ts. */
	impersonated_maker_id?: string | number
}

/**
 * Begins an impersonated session. THE only way to enter this state.
 *
 * Does all three things that have to happen together: blocks profile writes,
 * keeps the ADMIN identified (so their clicks stay theirs), and registers the
 * full impersonation labelling. Previously the plugin did all three on the
 * reload path while loginAsUser only blocked — so a non-reload impersonation was
 * labelled `is_impersonating: true` but with no impersonator_user_id or
 * impersonation_kind. The block is lifted by resetIdentity() on logout —
 * hub has no 'stop impersonating' control, so that is the only way back.
 */
export const beginImpersonation = (record: ImpersonationContext) => {
	// Blocked FIRST and unconditionally. A malformed record must never become a
	// reason to skip protecting the target's profile — that is the whole job.
	profileWritesBlocked = true
	if (!enabled) return

	if (!record?.impersonator_user_id) {
		// Can happen if state.user hasn't loaded when loginAsUser fires. We cannot
		// re-assert the admin's identity without their id, but the session must
		// still be LABELLED — otherwise it looks like a normal session with
		// mysteriously missing profile data, which is undiagnosable. Warn loudly
		// and label what we can.
		console.warn(
			"[analytics] impersonation began without an impersonator id — profile writes are " +
				"blocked and the session is labelled, but the admin's identity could not be re-asserted"
		)
		registerSuperProps({
			is_impersonating: true,
			impersonation_kind: record?.impersonation_kind,
		})
		return
	}

	try {
		// Re-assert the admin's distinct_id. On the reload path nothing has
		// identified yet; on the SPA path it is already correct and this is a
		// harmless no-op that makes the intent explicit.
		mixpanel.identify(record.impersonator_user_id)
		registerSuperProps({
			is_impersonating: true,
			impersonator_user_id: record.impersonator_user_id,
			impersonation_kind: record.impersonation_kind,
			impersonated_user_id: record.impersonated_user_id,
			impersonated_maker_id: record.impersonated_maker_id,
			// Past the TTL but still honoured (readImpersonation fails closed on age).
			// Surfaced so a session running far longer than expected is visible
			// rather than silently indistinguishable from a fresh one.
			is_stale_impersonation: record.is_stale === true,
		})
	} catch {
		/* no-op */
	}
}

export const resetIdentity = () => {
	clearTimers()
	lastEmitted.clear()
	profileWritesBlocked = false
	if (!enabled) return
	try {
		mixpanel.reset()
		// reset() clears super properties as well as the identity, and logout is
		// a client-side navigation — nothing re-runs initAnalytics. Without this,
		// every post-logout event (the /login page_viewed, any retry) would lose
		// app / environment / app_version and the explicit is_impersonating:false
		// that every report filters on.
		registerBaselineSuperProps()
	} catch {
		/* no-op */
	}
}

/* ------------------------------------------------------------------------- */
/* Group analytics (maker)                                                   */
/* ------------------------------------------------------------------------- */

export const GROUP_KEY = "maker_id"

/**
 * Maker users only. Admins belong to no maker, so `maker_id` stays a plain
 * event property for them and admin-created activity still rolls up to the
 * maker via event breakdown.
 *
 * Gated on VITE_MIXPANEL_GROUPS because the group key must be declared in the
 * Mixpanel project settings before the first set_group call, and group
 * analytics is a paid add-on on some plans.
 */
export const groupsEnabled = () => groupsOn

export const setMakerGroups = (
	makers: Array<{ id?: string | number; name?: string; slug?: string }> = []
) => {
	// profileWritesBlocked matters here as much as it does for people.set:
	// set_group writes against the *currently identified* distinct_id, which
	// during impersonation is the ADMIN's — so without this guard an admin gets
	// permanently tagged into the impersonated maker's group, corrupting both
	// their profile and that maker's group segmentation.
	if (!enabled || profileWritesBlocked || !groupsEnabled()) return

	const ids = makers.map((maker) => maker.id).filter((id): id is string | number => Boolean(id))
	if (!ids.length) return

	try {
		mixpanel.set_group(GROUP_KEY, ids)
		for (const maker of makers) {
			if (!maker.id) continue
			mixpanel
				.get_group(GROUP_KEY, maker.id)
				.set(pruneProps({ $name: maker.name, maker_slug: maker.slug }))
		}
	} catch {
		/* no-op */
	}
}

/* ------------------------------------------------------------------------- */
/* Tier 3 — Vuex write telemetry                                             */
/* ------------------------------------------------------------------------- */

/**
 * Reads, high-frequency and telemetry-hostile actions. An explicit Set rather
 * than a /^get/ regex: a regex lets getShareCart/getAuditLog through
 * inconsistently and would misclassify a future getOrCreateX.
 *
 * The debounced entries matter most. `utils/debounce.ts` returns undefined, so
 * Vuex's `after` hook fires SYNCHRONOUSLY at dispatch: duration_ms would always
 * be ~0, the error hook could never fire, and `before` fires once per keystroke
 * while the HTTP request fires once. That data would be fabricated.
 */
export const TIER3_DENYLIST: ReadonlySet<string> = new Set([
	// Auth / telemetry-hostile
	"logoutUser", // where reset() runs — tracking it races the reset
	"order/resetLedgers", // dispatched by logoutUser after reset() — same race
	"refreshToken", // fires on a timer from middleware/auth.global.ts
	"handleError", // itself a dispatched action; would double app_error_shown

	// Root reads
	"getShareCart",
	"getUsers",
	"filterUsers", // debounced
	"getUser",
	"getUserById",
	"getCuisines",
	"getAllergens",
	"getTags",
	"getCustomers", // debounced
	"getCustomerById",
	"filterCustomers", // debounced
	"getRewards",
	"getRewardById",
	"getDiscounts",
	"getDiscountById",
	"getMakerCategories",
	"getMakerCategoryById",
	"getMakers",
	"getMakersList",
	"getMakerById",
	"getMakerInsuranceDetails",
	"getDeliveryDays",
	"refreshDeliveryBooking", // the order page's live delivery poller, every 15s
	"getProductCategories",
	"getProductCategoriesList",
	"getProductCategoriesByMaker",
	"getProductCategoryById",
	"getPackages", // debounced
	"getPackageById",
	"getPackagesByMaker",
	"getOrganisations",
	"getOrganisation",
	"getOrganisationInvoiceConfig",
	"getSubscriptionConfigurations",
	"getPackageStructureByOrg",
	"getSubConfigById",
	"getSubscriptionTypes",
	"getPackageTypes",
	"getServerTime",
	"getAuditLog",
	"getActorLabel", // EntityTimeline Promise.all's over unique actors
	"fetchBulkAttachments",
	"fetchGeneratedInvoices",

	// Module reads
	"order/getOrderById",
	"order/getOrderLedgers",
	"order/getOrders", // debounced
	"order/getAnalyticsOrders",
	"order/calculateHubOrder", // debounced
	"order/validateOrder",
	"order/getShareCart",
	"product/getProducts", // debounced
	"product/getProductById",
	"product/calculatePrices", // fires per price keystroke
	"costCentre/getAllCostCentres",
	"settings/getSettings",
	"settings/getAllSettings",
	"settings/getSettingById",
	"addresses/getAddresses",
	"integrations/fetchIntegrations",
	"integrations/fetchXeroStatus",
	"xero/fetchXeroLink",
])

/** `action.type` arrives namespaced, e.g. "order/createOrder". */
export const splitActionType = (type: string) => {
	const slash = type.indexOf("/")
	if (slash === -1) return { module: "root", name: type }
	return { module: type.slice(0, slash), name: type.slice(slash + 1) }
}

export const shouldTrackAction = (type: string): boolean => {
	if (!type) return false
	if (TIER3_DENYLIST.has(type)) return false
	// Denylist entries are written unnamespaced for root actions; also match the
	// bare name so "order/getOrders" and "getOrders" both resolve.
	return !TIER3_DENYLIST.has(splitActionType(type).name)
}

/**
 * NOTE: never pass action.payload through to Mixpanel. Payloads carry full
 * order bodies, customer emails and auth config.
 */
export const trackStoreAction = (
	type: string,
	durationMs: number,
	pageRoute?: string,
	outcome?: "success" | "failed"
) => {
	if (!enabled || !shouldTrackAction(type)) return

	const { module, name } = splitActionType(type)
	track(EVENTS.STORE_ACTION_COMPLETED, {
		action_name: name,
		action_module: module,
		duration_ms: Math.round(durationMs),
		page_route: pageRoute,
		// "completed" means the dispatch settled, NOT that the write worked —
		// these actions resolve on failure. Filter on this, not on the event name.
		outcome,
	})
}

/* ------------------------------------------------------------------------- */
/* Init                                                                      */
/* ------------------------------------------------------------------------- */

/**
 * Session-independent super properties. Registered at init AND after every
 * reset(), because reset() clears super properties too.
 */
const registerBaselineSuperProps = () => {
	registerSuperProps({
		app: "hub",
		environment,
		app_version: import.meta.env.VITE_BUILD_SHA,
		// Always present with an explicit false so no report needs a
		// "does not exist" clause to filter staff-driven activity out.
		is_impersonating: false,
	})
}

export const initAnalytics = (): boolean => {
	const token = import.meta.env.VITE_MIXPANEL_TOKEN
	environment = resolveEnvironment(import.meta.env.VITE_AND_DINE_API)
	groupsOn = import.meta.env.VITE_MIXPANEL_GROUPS === "true"

	const debugOverride = import.meta.env.VITE_MIXPANEL_DEBUG === "true"
	if (!token || (environment !== "production" && !debugOverride)) {
		enabled = false
		// Say WHY, or a mis-set env var looks identical to "analytics is fine".
		// Both branches are one-line console output on a path that by definition
		// sends nothing, so this is the only way to diagnose it from a browser.
		if (!token) {
			console.info("[analytics] disabled: VITE_MIXPANEL_TOKEN is not set at build time")
		} else {
			console.info(
				`[analytics] disabled: environment resolved as "${environment}" from VITE_AND_DINE_API ` +
					`(tracking runs only when it resolves to "production", or with VITE_MIXPANEL_DEBUG=true)`
			)
		}
		return false
	}

	try {
		mixpanel.init(token, {
			debug: environment !== "production",
			// We own page views: this is an SPA, so the SDK's own pageview
			// tracking would fire once on boot and never again.
			track_pageview: false,
			persistence: "localStorage",
			// EU data residency — hub users are UK/EU staff and partners.
			api_host: "https://api-eu.mixpanel.com",
			// Session replay OFF, explicitly. mixpanel-browser bundles
			// @mixpanel/rrweb, and the hub renders real customer names, addresses,
			// phone numbers and order contents — recording those would put customer
			// PII in Mixpanel, which is well beyond what this integration is for.
			// Default is already 0; pinned so an SDK default change can't enable it.
			record_sessions_percent: 0,
		})
		enabled = true
	} catch {
		enabled = false
		return false
	}

	registerBaselineSuperProps()

	// $initial_referrer is registered ONCE by the SDK at first init and then
	// persisted, so the per-event scrub above never touches it. Rewrite it only
	// when it actually carries a query or hash — otherwise a genuine first-touch
	// referrer would be overwritten with the current one on every boot.
	try {
		const initialReferrer = mixpanel.get_property?.("$initial_referrer")
		if (typeof initialReferrer === "string" && /[?#]/.test(initialReferrer)) {
			const clean = sanitiseAnalyticsUrl(initialReferrer)
			// mixpanel.register directly, not registerSuperProps: pruneProps drops
			// "" and the unparseable case would then silently keep the raw value.
			if (clean) mixpanel.register({ $initial_referrer: clean })
			else mixpanel.unregister("$initial_referrer")
		}
	} catch {
		/* no-op */
	}

	return true
}

/** Test seam only — lets specs drive the facade without a real mixpanel.init. */
export const __setEnabledForTests = (
	value: boolean,
	env: Environment = "production",
	groups = false
) => {
	enabled = value
	environment = env
	groupsOn = groups
	profileWritesBlocked = false
	clearTimers()
	lastEmitted.clear()
}
